import { useContext, useEffect, useMemo, useRef } from 'react';
import ScrollToBottom from 'react-scroll-to-bottom';
import { ChatMessage } from '../../../Shared/API/ApiTypes';
import { AppContext } from '../../../App/AppContext';
import Message from './Message/Message';
import styles from './Messages.module.css';

type MessagesContentProps = {
  messages: ChatMessage[];
  firstMessageId: string;
};

const MessagesContent = ({ messages, firstMessageId }: MessagesContentProps) => (
  <>
    {messages.map((msg, index) => {
      const isFirstMessage = index === 0;

      return (
        <Message
          key={index}
          id={isFirstMessage ? firstMessageId : undefined}
          message={msg}
        />
      );
    })}
  </>
);

function Messages() {
  const { state } = useContext(AppContext);
  const conversationRef = useRef<HTMLDivElement | null>(null);
  const hasFocusedConversation = useRef(false);
  const isInitialMount = useRef(true);
  const firstMessageId = 'chat-initial-message';

  const messages = useMemo(() => state.messages ?? [], [state.messages]);

  useEffect(() => {
    const conversationNode = conversationRef.current;
    if (!conversationNode) {
      return;
    }

    const scrollContainer = conversationNode.parentElement?.parentElement ?? conversationNode.ownerDocument?.body;
    if (!scrollContainer) {
      return;
    }

    const updateFollowButtonAccessibility = () => {
      const followButtons = scrollContainer.querySelectorAll<HTMLButtonElement>(`.${styles.messagesScrollBtn}`);

      followButtons.forEach((button) => {
        if (button.tabIndex !== -1) {
          button.tabIndex = -1;
        }

        if (button.getAttribute('aria-hidden') !== 'true') {
          button.setAttribute('aria-hidden', 'true');
        }
      });
    };

    updateFollowButtonAccessibility();

    const observer = new MutationObserver(updateFollowButtonAccessibility);
    observer.observe(scrollContainer, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (messages.length === 0) {
      hasFocusedConversation.current = false;
      return;
    }

    // Skip focus on initial mount, only focus when new messages arrive
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (!hasFocusedConversation.current) {
      conversationRef.current?.focus({ preventScroll: true });
      hasFocusedConversation.current = true;
    }
  }, [messages.length]);

  const describedBy = messages.length > 0 ? firstMessageId : undefined;

  return (
    <ScrollToBottom className={styles.messagesWrapper} followButtonClassName={styles.messagesScrollBtn}>
      <div
        aria-describedby={describedBy}
        aria-label="Chat conversation"
        aria-live="polite"
        className={styles.conversation}
        ref={conversationRef}
        role="log"
        tabIndex={-1}
      >
        <MessagesContent messages={messages} firstMessageId={firstMessageId} />
      </div>
    </ScrollToBottom>
  );
}

export default Messages;
